import { useEffect, useState } from "react";
import type {
  SubscriptionChangeInput,
  Subscription,
  SubscriptionActionPreview,
} from "@vereinfach/types";
import { api, mutate, errorMessage } from "../lib/api";
import { formatCHF, formatPeriodRange } from "../lib/format";
import { Modal } from "./Modal";
export function SubscriptionActionDialog({
  tenantId,
  subscription,
  onClose,
  onDone,
}: {
  tenantId: string;
  subscription: Subscription;
  onClose: () => void;
  onDone: () => void;
}) {
  const [days, setDays] = useState("");
  const [extension, setExtension] = useState<"none" | "elapsed" | "planned">("elapsed");
  const [preview, setPreview] = useState<SubscriptionActionPreview | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const change: SubscriptionChangeInput =
    subscription.status === "paused"
      ? { kind: "resume", extension }
      : { kind: "pause", ...(days ? { plannedDays: Number(days) } : {}) };
  const key = JSON.stringify(change);
  useEffect(() => {
    let active = true;
    setPreview(null);
    setError("");
    const timer = setTimeout(() => {
      api<SubscriptionActionPreview>(
        `/tenants/${tenantId}/subscriptions/${subscription.id}/preview`,
        { method: "POST", body: JSON.stringify({ change: JSON.parse(key) }) },
      )
        .then((p) => {
          if (active) setPreview(p);
        })
        .catch((e) => {
          if (active) setError(errorMessage(e));
        });
    }, 200);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [key, tenantId, subscription.id]);
  async function commit() {
    if (!preview) return;
    setBusy(true);
    try {
      await mutate(`/tenants/${tenantId}/subscriptions/${subscription.id}/commit`, {
        change,
        revision: preview.revision,
        confirmed: true,
      });
      onDone();
      onClose();
    } catch (e) {
      setError(errorMessage(e));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={subscription.status === "paused" ? "Abo fortsetzen" : "Abo pausieren"}
      onClose={onClose}
    >
      <div className="space-y-5">
        {subscription.status === "paused" ? (
          <label className="block">
            Pausenzeit anrechnen
            <select
              className="input mt-2"
              value={extension}
              onChange={(e) => setExtension(e.target.value as typeof extension)}
            >
              <option value="elapsed">Um die bisherige Pausenzeit verlängern</option>
              <option value="none">Ohne zusätzliche Verlängerung fortsetzen</option>
              {subscription.planned_pause_days && (
                <option value="planned">Um die gesamte geplante Pause verlängern</option>
              )}
            </select>
          </label>
        ) : (
          <label className="block">
            Geplante Pausentage (optional)
            <input
              type="number"
              min="1"
              max="3660"
              className="input mt-2"
              value={days}
              onChange={(e) => setDays(e.target.value)}
            />
          </label>
        )}
        {preview && (
          <div className="rounded-lg bg-slate-50 p-4 space-y-2">
            <p>
              Bisher: {formatPeriodRange(preview.before.current_start, preview.before.current_end)}
            </p>
            <p>
              Danach: {formatPeriodRange(preview.after.current_start, preview.after.current_end)}
            </p>
            <p>
              Status danach:{" "}
              {{ active: "Aktiv", paused: "Pausiert", ended: "Beendet" }[preview.after.status]}
            </p>
            {preview.extension_days > 0 && (
              <p>{preview.extension_days} zusätzliche Tage ohne Berechnung.</p>
            )}
            <p>{preview.retracted_invoice_ids.length} künftige Entwürfe werden zurückgezogen.</p>
            {preview.created_drafts.length > 0 && (
              <div className="space-y-2 text-amber-800">
                <p>{preview.created_drafts.length} neue Rechnungsentwürfe werden erstellt:</p>
                <ul className="list-disc pl-5">
                  {preview.created_drafts.map((draft, i) => (
                    <li key={i}>
                      {formatCHF(draft.total_rappen)} ·{" "}
                      {draft.coverage
                        .map((c) => formatPeriodRange(c.period_start, c.period_end))
                        .join(", ")}
                    </li>
                  ))}
                </ul>
                <p>Die Entwürfe müssen separat freigegeben werden.</p>
              </div>
            )}
            {preview.retained_invoice_ids.length > 0 && (
              <p className="text-amber-800">
                {preview.retained_invoice_ids.length} bereits freigegebene Rechnungen bleiben
                bestehen. Bitte prüfen.
              </p>
            )}
          </div>
        )}
        {error && (
          <p role="alert" className="text-red-700">
            {error}
          </p>
        )}
        <button
          className="btn-primary disabled:opacity-50"
          disabled={!preview || busy}
          onClick={commit}
        >
          {busy ? "Wird gespeichert…" : "Änderung bestätigen"}
        </button>
      </div>
    </Modal>
  );
}
